/**
 * «Tareas pendientes»: todo lo que espera una decisión de Jorge, en una lista.
 *
 * Una línea por cosa —cliente, qué es, cuánto toca, riesgo— y tres botones.
 * El orden lo decide el servidor: primero lo que caduca antes, después por
 * riesgo. Aquí no se reordena; si la pantalla ordenara por su cuenta, el
 * número de la pestaña y la lista podrían contar cosas distintas.
 *
 * ── Por qué «Luego» pregunta y «No» no obliga ───────────────────────────────
 *
 * «Luego» necesita un cuándo y no se adivina: un aplazamiento mal entendido es
 * una tarea que desaparece hasta una fecha que nadie quiso. «No» acepta un
 * motivo y se conforma sin él — exigirlo haría que la salida honesta («no me
 * convence y no sé explicarlo») fuese más cara que no decidir.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import "./paneles.css";
import { useDialogo } from "./dialogo";

/**
 * Las llamadas van con `fetch` como en las demás pantallas: `api` es una lista
 * de métodos con nombre, no un cliente genérico, y añadirle cinco entradas
 * para una pantalla sería hacerle sitio a algo que ya tiene sitio.
 */
async function pedir<T>(ruta: string, init?: RequestInit): Promise<T> {
  const r = await fetch(ruta, {
    ...init,
    headers: init?.body ? { "content-type": "application/json" } : undefined,
    credentials: "same-origin",
  });
  const texto = await r.text();
  const cuerpo = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};
  if (!r.ok) throw new Error(typeof cuerpo.error === "string" ? cuerpo.error : `Error ${r.status}`);
  return cuerpo as T;
}

export interface Tarea {
  tipo: "aprobacion" | "hipotesis" | "regla" | "plan";
  id: string;
  cliente: string | null;
  clienteId: string | null;
  que: string;
  /** El dominio donde se toca. No es el nombre del cliente. */
  donde?: string | null;
  cuanto: string | null;
  riesgo: "bajo" | "medio" | "alto";
  caduca: string | null;
  aplazadaHasta: string | null;
  vecesAplazada: number;
  familia: string | null;
  desde: string;
  sinMasAplazar: boolean;
  /** Aviso fijo de su familia, si lo tiene. */
  aviso: string | null;
  /**
   * Qué hay dentro, una línea por acción. Vacío en lo que no es un lote.
   *
   * Lo manda el servidor sacándolo del trabajo que viaja en la propia tarjeta.
   */
  acciones?: AccionDeLaTarjeta[];
  /**
   * Si se puede decidir o sólo mirar. Lo decide el SERVIDOR, no esta pantalla.
   *
   * Aquí sólo se pinta. La regla —un lote o un plan sin su lista no se
   * aprueba— vive en un sitio y la ruta se niega igual: esconder un botón no
   * es un candado.
   */
  sePuedeDecidir: boolean;
  /** Y por qué no, con las palabras del servidor. */
  porQueNoSeDecide: string | null;
  /** De qué va: actualizaciones, SEO, captación… La pone el servidor. */
  categoria?: string;
}

interface Categoria {
  id: string;
  nombre: string;
}

/** Una línea de lo que hay dentro de una tarjeta de lote o de plan. */
export interface AccionDeLaTarjeta {
  que: string;
  donde: string | null;
  cambia: string | null;
}

export interface UnGrupoDeTrabajo {
  clave: string;
  comoSeLlama: string;
  fichas: number;
  webs: number;
  cuantas: number | null;
  unidad: string | null;
  sinContar: number;
  riesgo: Tarea["riesgo"];
  caducaPronto: boolean;
}

export interface LoQueSeVaAHacer {
  grupos: UnGrupoDeTrabajo[];
  webs: number;
  clientes: number;
  fichas: number;
}

export interface Recuento {
  ahora: number;
  aplazadas: number;
  sinMasAplazar: number;
  caducanPronto: number;
  porTipo: Record<string, number>;
}

const COMO_SE_LLAMA: Record<Tarea["tipo"], string> = {
  aprobacion: "Sí de arreglo",
  hipotesis: "Hipótesis",
  regla: "Regla",
  plan: "Plan del mes",
};

/** «caduca en 3 h», «caduca mañana», «Caducada». */
function cuantoLeQueda(caduca: string | null): { texto: string; aprieta: boolean; vencida: boolean } | null {
  if (!caduca) return null;
  const ms = new Date(caduca).getTime() - Date.now();
  if (ms <= 0) return { texto: "Caducada", aprieta: true, vencida: true };
  const horas = ms / 3_600_000;
  if (horas < 1) return { texto: `Caduca en ${Math.round(ms / 60_000)} min`, aprieta: true, vencida: false };
  if (horas < 24) return { texto: `Caduca en ${Math.round(horas)} h`, aprieta: true, vencida: false };
  const dias = Math.round(horas / 24);
  return { texto: `Caduca en ${dias} día${dias === 1 ? "" : "s"}`, aprieta: dias <= 2, vencida: false };
}

/** Los «luego» de siempre, en las palabras que entiende el servidor. */
const LUEGOS = [
  ["mañana", "Mañana"],
  ["3 días", "3 días"],
  ["una semana", "1 semana"],
] as const;

/**
 * Lo que se le está preguntando a una tarjeta, en la propia tarjeta.
 *
 * Antes eran `window.prompt` y `window.confirm`: ventanas del navegador que tapan
 * la pantalla, no dicen de qué tarjeta son y en el móvil salen a medias.
 */
type Pregunta = { clave: string; que: "si" | "luego" | "no" } | null;

export function TareasPendientes({ alCambiar }: { alCambiar?: () => void }) {
  const [tareas, setTareas] = useState<Tarea[]>([]);
  const [aplazadas, setAplazadas] = useState<Tarea[]>([]);
  const [recuento, setRecuento] = useState<Recuento | null>(null);
  const [resumen, setResumen] = useState<LoQueSeVaAHacer | null>(null);
  /** El grupo del resumen en el que se ha pinchado, para filtrar la lista. */
  const [porGrupo, setPorGrupo] = useState<string>("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState<string | null>(null);
  const [verAplazadas, setVerAplazadas] = useState(false);
  const [porCliente, setPorCliente] = useState<string>("");
  const [porTipo, setPorTipo] = useState<string>("");
  /** La categoría elegida arriba; vacía, todas, agrupadas. */
  const [porCategoria, setPorCategoria] = useState<string>("");
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  /**
   * Cuál tiene abierta su lista. Una cada vez, y cerrada al empezar.
   *
   * Cerrada porque la tarjeta de okeyhosteleria trae 239 acciones y trece
   * tarjetas abiertas no son una lista de decisiones. Y una cada vez porque la
   * lista sólo se monta cuando se abre: así lo que cuesta pintarla se paga al
   * mirarla, no al entrar en la pantalla.
   */
  const [abierta, setAbierta] = useState<string | null>(null);

  const traer = useCallback(async () => {
    try {
      const r = await pedir<{
        recuento: Recuento;
        resumen: LoQueSeVaAHacer;
        tareas: Tarea[];
        aplazadas: Tarea[];
        categorias?: Categoria[];
      }>("/api/tareas-pendientes");
      setTareas(r.tareas);
      setCategorias(r.categorias ?? []);
      setAplazadas(r.aplazadas);
      setRecuento(r.recuento);
      setResumen(r.resumen);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void traer();
  }, [traer]);

  /** Qué se le está preguntando, y a qué tarjeta. Una cada vez. */
  const [pregunta, setPregunta] = useState<Pregunta>(null);
  const [motivo, setMotivo] = useState("");
  const [otraFecha, setOtraFecha] = useState("");
  /** Lo que acaba de pasar, arriba y hasta que se cierre. */
  const [nota, setNota] = useState<{ texto: string; mal?: boolean } | null>(null);

  function preguntar(t: Tarea, que: "si" | "luego" | "no") {
    setPregunta({ clave: `${t.tipo}:${t.id}`, que });
    setMotivo("");
    setOtraFecha("");
  }

  const { preguntar: confirmar, dialogo } = useDialogo();
  const [enLote, setEnLote] = useState<{ hechas: number; total: number } | null>(null);

  /**
   * Aprobar todas las actualizaciones de plugins de golpe (Jorge, 23/10/2026).
   *
   * Es lo mismo que pulsar «Sí» en cada una, una detrás de otra: cada tarjeta
   * sigue con su lista exacta, sus copias antes de cada plugin y su vuelta
   * atrás sola. Se paran al primer fallo para no dejar media lista sin saber.
   */
  async function aprobarLasActualizaciones() {
    const lista = actualizaciones;
    if (lista.length === 0) return;
    const plugins = lista.reduce((n, t) => n + (t.acciones?.length ?? 0), 0);
    const ok = await confirmar(
      `¿Aprobar las ${lista.length} actualizaciones (${plugins} plugins en ${lista.length} webs)? Sólo versiones menores, con copia antes de cada plugin y vuelta atrás sola si una web cae. WiPrex no se toca.`,
      `Aprobar las ${lista.length}`,
    );
    if (!ok) return;
    let hechas = 0;
    setEnLote({ hechas, total: lista.length });
    for (const t of lista) {
      try {
        await pedir(`/api/tareas-pendientes/${t.tipo}/${encodeURIComponent(t.id)}/si`, {
          method: "POST",
          body: JSON.stringify({}),
        });
        hechas++;
        setEnLote({ hechas, total: lista.length });
      } catch (e) {
        setNota({ texto: `Aprobadas ${hechas} de ${lista.length}. Se paró en ${t.donde ?? t.que}: ${(e as Error).message}`, mal: true });
        setEnLote(null);
        await traer();
        alCambiar?.();
        return;
      }
    }
    setEnLote(null);
    setNota({ texto: `Aprobadas las ${hechas} actualizaciones. Se hacen una web detrás de otra; el resultado de cada una sale en Control.` });
    await traer();
    alCambiar?.();
  }

  async function decidir(t: Tarea, que: "si" | "luego" | "no", cuerpo: Record<string, string> = {}) {
    setTrabajando(`${t.tipo}:${t.id}`);
    try {
      const r = await pedir<{ ok?: boolean; error?: string; avisa?: string; reglaPropuesta?: string | null }>(
        `/api/tareas-pendientes/${t.tipo}/${encodeURIComponent(t.id)}/${que}`,
        { method: "POST", body: JSON.stringify(cuerpo) },
      );
      const hecho =
        que === "si" ? "Aprobado" : que === "no" ? "Rechazado" : `Aplazado hasta ${cuerpo.hasta ?? "luego"}`;
      setNota({
        texto: [
          `${hecho}: ${t.que}.`,
          r.avisa ?? "",
          r.reglaPropuesta
            ? `Van dos noes a «${r.reglaPropuesta}»: te he dejado una hipótesis de regla de la casa en esta lista.`
            : "",
        ]
          .filter(Boolean)
          .join(" "),
      });
      setPregunta(null);
      await traer();
      alCambiar?.();
    } catch (e) {
      setNota({ texto: `No se pudo: ${(e as Error).message}`, mal: true });
    } finally {
      setTrabajando(null);
    }
  }

  const clientes = [...new Set(tareas.map((t) => t.cliente).filter((c): c is string => Boolean(c)))].sort();
  /*
   * La clave de grupo se calcula igual que en el servidor —familia, y si no,
   * el tipo— para que pinchar una tarjeta del resumen enseñe exactamente lo
   * que esa tarjeta contó. Si las dos reglas se separaran, el resumen diría
   * «3» y la lista enseñaría otra cosa, que es peor que no poder pinchar.
   */
  const grupoDe = (t: Tarea) =>
    t.familia ?? (t.tipo === "aprobacion" && t.categoria ? `cat:${t.categoria}` : `tipo:${t.tipo}`);
  const orden = (t: Tarea) => {
    const i = categorias.findIndex((c) => c.id === t.categoria);
    return i < 0 ? categorias.length : i;
  };
  const deLaLista = verAplazadas ? aplazadas : tareas;
  const aLaVista = deLaLista
    .filter(
      (t) =>
        (!porCliente || t.cliente === porCliente) &&
        (!porTipo || t.tipo === porTipo) &&
        (!porGrupo || grupoDe(t) === porGrupo) &&
        (!porCategoria || t.categoria === porCategoria),
    )
    /* Agrupadas por categoría; dentro, en el orden que manda el servidor. */
    .map((t, i) => ({ t, i }))
    .sort((a, b) => orden(a.t) - orden(b.t) || a.i - b.i)
    .map(({ t }) => t);
  const cuantasDe = (id: string) => deLaLista.filter((t) => t.categoria === id).length;
  /** Las actualizaciones que se pueden aprobar de una vez (23/10/2026). */
  const actualizaciones = tareas.filter((t) => t.categoria === "actualizaciones" && t.tipo === "aprobacion" && t.sePuedeDecidir);
  const nombreDe = (id: string | undefined) => categorias.find((c) => c.id === id)?.nombre ?? "Otras";
  const tiposQueHay = (Object.keys(COMO_SE_LLAMA) as Tarea["tipo"][]).filter((t) => (recuento?.porTipo[t] ?? 0) > 0);

  if (cargando)
    return (
      <div className="pnl">
        <p className="pnl-sub">Mirando qué espera…</p>
      </div>
    );

  return (
    <div className="pnl">
      <header className="pnl-cabecera">
        <div>
          <h1>Tareas pendientes</h1>
          {recuento && (
            <p className="pnl-sub">
              {recuento.ahora === 0 ? (
                "Nada espera tu decisión."
              ) : (
                <>
                  <strong>{recuento.ahora}</strong> {recuento.ahora === 1 ? "espera" : "esperan"} tu decisión
                </>
              )}
              {recuento.caducanPronto > 0 && (
                <>
                  {" · "}
                  <span className="es-mal">
                    {recuento.caducanPronto} {recuento.caducanPronto === 1 ? "caduca" : "caducan"} en menos de dos días
                  </span>
                </>
              )}
              {recuento.sinMasAplazar > 0 && ` · ${recuento.sinMasAplazar} ya no admiten otro «luego»`}
            </p>
          )}
        </div>
        {recuento && recuento.aplazadas > 0 && (
          <div className="pnl-vistas" role="group" aria-label="Qué lista ver">
            <button aria-pressed={!verAplazadas} onClick={() => setVerAplazadas(false)}>
              Ahora <span className="n">{recuento.ahora}</span>
            </button>
            <button aria-pressed={verAplazadas} onClick={() => setVerAplazadas(true)}>
              Aplazadas <span className="n">{recuento.aplazadas}</span>
            </button>
          </div>
        )}
      </header>

      {error && (
        <div className="pnl-nota es-mal" role="alert">
          <p>No he podido leerlas: {error}</p>
        </div>
      )}
      {nota && (
        <div className={nota.mal ? "pnl-nota es-mal" : "pnl-nota"} role="status">
          <p>{nota.texto}</p>
          <button aria-label="Cerrar" onClick={() => setNota(null)}>
            ×
          </button>
        </div>
      )}

      {/*
        El resumen de lo que se va a hacer.
        ───────────────────────────────────
        Arriba y antes de los filtros porque es lo que se pregunta al abrir:
        no «cuántas fichas hay» —eso está en el número de la pestaña— sino
        **qué trabajo es**. Agrupado por el trabajo de verdad (títulos, alt,
        redirecciones), no por cómo está guardado. Pinchar uno filtra la lista.

        Sólo cuando NO se están mirando las aplazadas: el resumen cuenta lo
        que espera ahora.
      */}
      {!verAplazadas && resumen && resumen.grupos.length > 0 && (
        <section aria-label="Lo que se va a hacer">
          <h2 className="tp2-grupos-titulo">
            Lo que se va a hacer
            <span>
              {resumen.fichas} {resumen.fichas === 1 ? "cosa" : "cosas"}
              {resumen.webs > 0 && ` · ${resumen.webs} ${resumen.webs === 1 ? "web" : "webs"}`}
            </span>
          </h2>
          <div className="tp2-grupos">
            {resumen.grupos.map((g) => {
              const puesta = porGrupo === g.clave;
              const estado = g.riesgo === "alto" ? "mal" : g.riesgo === "medio" ? "aviso" : "ok";
              return (
                <button
                  key={g.clave}
                  type="button"
                  className={`pnl-cifra es-${estado}`}
                  aria-pressed={puesta}
                  onClick={() => setPorGrupo(puesta ? "" : g.clave)}
                  title={puesta ? "Quitar el filtro" : `Ver sólo ${g.comoSeLlama.toLowerCase()}`}
                >
                  <span className="n">
                    {(g.cuantas ?? g.fichas).toLocaleString("es-ES")}
                    <span className="u">{g.cuantas !== null ? g.unidad : g.fichas === 1 ? "cosa" : "cosas"}</span>
                  </span>
                  <span className="l">{g.comoSeLlama}</span>
                  {/*
                    Lo que no trae número se dice, no desaparece: un total
                    corto que no avisa es con lo que se acaba decidiendo.
                  */}
                  {(g.webs > 1 || g.caducaPronto || (g.cuantas !== null && g.sinContar > 0)) && (
                    <span className="x">
                      {[
                        g.webs > 1 ? `${g.webs} webs` : "",
                        g.cuantas !== null && g.sinContar > 0 ? `y ${g.sinContar} sin contar` : "",
                        g.caducaPronto ? "algo caduca pronto" : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {dialogo}
      {!verAplazadas && actualizaciones.length > 1 && (porCategoria === "" || porCategoria === "actualizaciones") && (
        <div className="tp2-lote">
          <span>
            <strong>{actualizaciones.length} actualizaciones de plugins</strong> esperan, una por web.
          </span>
          <button
            type="button"
            className="pnl-boton si"
            disabled={enLote !== null}
            onClick={() => void aprobarLasActualizaciones()}
          >
            {enLote ? `Aprobando ${enLote.hechas} de ${enLote.total}…` : `Aprobar las ${actualizaciones.length}`}
          </button>
        </div>
      )}
      {categorias.filter((c) => cuantasDe(c.id) > 0).length > 1 && (
        <div className="pnl-filtros tp2-categorias" role="tablist" aria-label="Categorías">
          <button className="pnl-chip" role="tab" aria-pressed={!porCategoria} onClick={() => setPorCategoria("")}>
            Todas <span className="n">{deLaLista.length}</span>
          </button>
          {categorias
            .filter((c) => cuantasDe(c.id) > 0)
            .map((c) => (
              <button
                key={c.id}
                className="pnl-chip"
                role="tab"
                aria-pressed={porCategoria === c.id}
                onClick={() => setPorCategoria(porCategoria === c.id ? "" : c.id)}
              >
                {c.nombre} <span className="n">{cuantasDe(c.id)}</span>
              </button>
            ))}
        </div>
      )}

      {(clientes.length > 1 || porGrupo) && (
        <div className="pnl-filtros" role="group" aria-label="Filtrar">
          {porGrupo && (
            <button className="pnl-chip" aria-pressed onClick={() => setPorGrupo("")}>
              Quitar el filtro ×
            </button>
          )}
          {clientes.length > 1 && (
            <select
              className="pnl-select"
              value={porCliente}
              onChange={(e) => setPorCliente(e.target.value)}
              aria-label="Cliente"
            >
              <option value="">Todos los clientes</option>
              {clientes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {aLaVista.length === 0 ? (
        <div className="tp2-vacio">
          <strong>{verAplazadas ? "Nada aplazado" : "Todo decidido"}</strong>
          {verAplazadas ? "No hay nada esperando a otro día." : "Cuando BilboBot necesite un sí, aparecerá aquí."}
        </div>
      ) : (
        <ul className="tp2-lista">
          {aLaVista.map((t, i) => {
            const queda = cuantoLeQueda(t.caduca);
            const clave = `${t.tipo}:${t.id}`;
            const ocupada = trabajando === clave;
            const dentro = t.acciones ?? [];
            const seVe = abierta === clave;
            const preguntando = pregunta?.clave === clave ? pregunta.que : null;
            /* El cuánto sólo si no está ya dicho: la fila decía «el SEO de 9 página(s)» y al lado «9 páginas». */
            const cuanto = t.cuanto && !t.que.includes(t.cuanto) ? t.cuanto : null;
            /* Un encabezado cada vez que cambia la categoría, si se ven todas. */
            const cabecera =
              !porCategoria && categorias.length > 0 && (i === 0 || aLaVista[i - 1]!.categoria !== t.categoria);
            return (
              <Fragment key={clave}>
                {cabecera && (
                  <li className="tp2-seccion" aria-hidden="false">
                    <span>{nombreDe(t.categoria)}</span>
                    <span className="n">{aLaVista.filter((x) => x.categoria === t.categoria).length}</span>
                  </li>
                )}
              <li className={`tp2-tarjeta ${t.riesgo}${queda?.aprieta ? " aprieta" : ""}`}>
                <div className="tp2-arriba">
                  {/*
                    El dominio al lado del cliente, porque no son lo mismo:
                    ELECTROBILBAO es okeyhosteleria.com.
                  */}
                  {t.cliente ? (
                    <span className="tp2-cliente">{t.cliente}</span>
                  ) : (
                    <span className="tp2-sin-cliente">Para toda la casa</span>
                  )}
                  {t.donde && <span className="tp2-donde">{t.donde}</span>}
                  {(queda || (verAplazadas && t.aplazadaHasta)) && (
                    <span className={`tp2-plazo${queda?.vencida ? " vencida" : queda?.aprieta ? " aprieta" : ""}`}>
                      {verAplazadas && t.aplazadaHasta
                        ? `Vuelve el ${new Date(t.aplazadaHasta).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}`
                        : queda?.texto}
                    </span>
                  )}
                </div>

                <p className="tp2-que">{t.que}</p>

                <div className="tp2-etiquetas">
                  {/* El tipo sólo cuando NO es un sí de arreglo: son nueve de cada diez y no distinguía nada. */}
                  {t.tipo !== "aprobacion" && <span className="tp2-etiqueta">{COMO_SE_LLAMA[t.tipo]}</span>}
                  {cuanto && <span className="tp2-etiqueta">{cuanto}</span>}
                  <span className="tp2-etiqueta riesgo">riesgo {t.riesgo}</span>
                  {t.sinMasAplazar && <span className="tp2-etiqueta">ya se aplazó: toca decidir</span>}
                </div>

                {t.aviso && (
                  <p className="tp2-aviso" role="note">
                    {t.aviso}
                  </p>
                )}

                {/*
                  Lo que hay dentro, cerrado al entrar: la de okeyhosteleria trae
                  239 acciones y trece tarjetas abiertas no son una lista de
                  decisiones.
                */}
                {seVe && dentro.length > 0 && (
                  <ol className="tp2-acciones">
                    {dentro.map((a, i) => (
                      <li key={`${a.que}|${a.donde ?? ""}|${i}`}>
                        <span className="i">{i + 1}</span>
                        <span className="q">{a.que}</span>
                        {a.donde && <span className="d">{a.donde}</span>}
                        {a.cambia && <span className="c">{a.cambia}</span>}
                      </li>
                    ))}
                  </ol>
                )}

                <div className="tp2-abajo">
                  {dentro.length > 0 && (
                    <button
                      type="button"
                      className="pnl-boton plano"
                      aria-expanded={seVe}
                      onClick={() => setAbierta(seVe ? null : clave)}
                    >
                      {seVe ? "Ocultar" : "Ver"} {dentro.length === 1 ? "la acción" : `las ${dentro.length} acciones`}
                    </button>
                  )}
                  {/*
                    Sin la lista dentro, el «Sí» no sale: decir que sí a «239
                    páginas» es decir que sí a un número, y la ruta se niega
                    igual. «Luego» y «No» sí salen: rechazar no toca ninguna
                    web, y sin ellos una tarjeta sin lista no se podría quitar.
                  */}
                  {!t.sePuedeDecidir && !preguntando && (
                    <span className="tp2-motivo" role="note">
                      {t.porQueNoSeDecide ?? "Sin la lista de lo que hay dentro, no se decide."}
                    </span>
                  )}

                  {preguntando === "si" ? (
                    <div className="tp2-pregunta">
                      <span className="q">¿Lo apruebas?</span>
                      <button className="pnl-boton" disabled={ocupada} onClick={() => setPregunta(null)}>
                        Cancelar
                      </button>
                      <button className="pnl-boton si" disabled={ocupada} onClick={() => void decidir(t, "si")}>
                        {ocupada ? "Aprobando…" : "Sí, aprobar"}
                      </button>
                    </div>
                  ) : preguntando === "luego" ? (
                    <div className="tp2-pregunta">
                      <span className="q">¿Hasta cuándo?</span>
                      {LUEGOS.map(([valor, nombre]) => (
                        <button
                          key={valor}
                          className="pnl-boton"
                          disabled={ocupada}
                          onClick={() => void decidir(t, "luego", { hasta: valor })}
                        >
                          {nombre}
                        </button>
                      ))}
                      <input
                        type="date"
                        value={otraFecha}
                        aria-label="Otra fecha"
                        onChange={(e) => setOtraFecha(e.target.value)}
                        style={{ minWidth: 0 }}
                      />
                      {otraFecha && (
                        <button
                          className="pnl-boton si"
                          disabled={ocupada}
                          onClick={() => void decidir(t, "luego", { hasta: otraFecha })}
                        >
                          Aplazar
                        </button>
                      )}
                      <button className="pnl-boton plano" onClick={() => setPregunta(null)}>
                        Cancelar
                      </button>
                    </div>
                  ) : preguntando === "no" ? (
                    <form
                      className="tp2-pregunta"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void decidir(t, "no", { motivo });
                      }}
                    >
                      {/* El motivo se acepta y no se exige: «no me convence» también es una respuesta. */}
                      <input
                        autoFocus
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        placeholder="Por qué no (opcional)"
                        maxLength={300}
                      />
                      <button type="button" className="pnl-boton plano" onClick={() => setPregunta(null)}>
                        Cancelar
                      </button>
                      <button type="submit" className="pnl-boton no" disabled={ocupada}>
                        {ocupada ? "Rechazando…" : "Rechazar"}
                      </button>
                    </form>
                  ) : (
                    <div className="tp2-botones">
                      <button className="pnl-boton no" disabled={ocupada} onClick={() => preguntar(t, "no")}>
                        No
                      </button>
                      <button
                        className="pnl-boton"
                        disabled={ocupada || t.sinMasAplazar}
                        onClick={() => preguntar(t, "luego")}
                        title={t.sinMasAplazar ? "Ya se aplazó una vez: toca decidir" : "Mañana, 3 días o una fecha"}
                      >
                        Luego
                      </button>
                      {t.sePuedeDecidir && (
                        <button className="pnl-boton si" disabled={ocupada} onClick={() => preguntar(t, "si")}>
                          Sí
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </li>
              </Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
}
